import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Game } from 'modules/games/domain/interfaces/game';
import { baseApi } from 'shared/api/base-api';
import { RootState } from 'store/store';

import { Championship } from 'modules/championship/domain/interfaces/championship';
import { ChampionshipSettingDto } from 'modules/championship/domain/interfaces/championship-setting-dto';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { SymbolicTeam } from 'modules/championship/domain/interfaces/symbolic-team';
import { UpsertChampionshipDto } from 'modules/championship/domain/interfaces/upsert-championship-dto';
import championshipRepository, {
  ChampionshipTeamsWithApplicationsDto,
  UpsertChampionshipParams,
} from 'modules/championship/domain/repositories/championship.repository';
import championshipGameRepository, { UpsertGameDto } from 'modules/championship/domain/repositories/championship-game.repository';
import symbolicTeamRepository, { UpsertSymbolicTeamDto } from 'modules/championship/domain/repositories/symbolic-team.repository';

const PREFIX = 'championship';

export const fetchChampionship = createAsyncThunk<Championship | null, number>(
  `${PREFIX}/fetchChampionship`,
  async (championshipId) => {
    try {
      return await championshipRepository.fetchChampionshipById(championshipId);
    } catch (err) {
      console.error(`${PREFIX}/fetchChampionship error:`, err);
      return null;
    }
  },
);

export const fetchChampionshipTeams = createAsyncThunk<ChampionshipTeamsWithApplicationsDto, number>(
  `${PREFIX}/fetchChampionshipTeams`,
  async (championshipId) => {
    try {
      return await championshipRepository.fetchChampionshipTeamsWithApplications(championshipId);
    } catch (err) {
      console.error(`${PREFIX}/fetchChampionshipTeams error:`, err);
      throw err;
    }
  },
);

export const fetchChampionshipSettings = createAsyncThunk<
  { championshipId: number; settings: ChampionshipSettings; settingsMap: ChampionshipSettingDto[] },
  number
>(`${PREFIX}/fetchChampionshipSettings`, async (championshipId) => {
  try {
    const result = await championshipRepository.fetchChampionshipSettings(championshipId);
    return { championshipId, ...result };
  } catch (err) {
    console.error(`${PREFIX}/fetchChampionshipSettings error:`, err);
    throw err;
  }
});

export const upsertChampionshipGame = createAsyncThunk<Game, UpsertGameDto>(`${PREFIX}/upsertChampionshipGame`, async (payload) => {
  try {
    return await championshipGameRepository.upsertGame(payload);
  } catch (err) {
    console.error(`${PREFIX}/upsertChampionshipGame error:`, err);
    throw err;
  }
});

export const deleteChampionshipGame = createAsyncThunk<number | null, { gameId: number; championshipId: number }>(
  `${PREFIX}/deleteChampionshipGame`,
  async (payload) => {
    try {
      return await championshipGameRepository.deleteGame(payload.gameId);
    } catch (err) {
      console.error(`${PREFIX}/deleteGame error:`, err);
      return null;
    }
  },
);

export const clearSelectedChampionship = createAction<void>(`${PREFIX}/clearSelectedChampionship`);

export const upsertChampionship = createAsyncThunk<number, UpsertChampionshipDto, { state: RootState }>(
  `${PREFIX}/upsertChampionship`,
  async (params, { dispatch, getState }) => {
    try {
      const state = getState();
      const newSettings = {
        ...state.championships.championship.data?.byId[params?.id ?? -1]?.settings,
        overlayId: params.overlayId,
      } as ChampionshipSettings;

      if (params.teamPlayersNumber) {
        newSettings.teamPlayersNumber = params.teamPlayersNumber;
      }

      let teamIds: number[] = [];
      if (params.id) {
        const championship = state.championships.championship.data!.byId[params.id];
        teamIds = championship.teams.map((team) => team.id);
      }

      const championshipId = await championshipRepository.upsertChampionship({
        id: params.id,
        tournamentId: params.tournamentId,
        name: params.name,
        description: params.description,
        startDate: params.startDate,
        endDate: params.endDate,
        address: params.address,
        settings: newSettings,
        locationIds: params.locationIds,
        teamIds,
      });

      dispatch(fetchChampionship(championshipId));
      // Инвалидируем список чемпионатов в RTK Query, чтобы новый чемпионат появился в списке
      dispatch(baseApi.util.invalidateTags([{ type: 'Championship', id: 'PARTIAL-LIST' }]));

      return championshipId;
    } catch (err) {
      console.error(`${PREFIX}/upsertChampionship error:`, err);
      throw err;
    }
  },
);

export const upsertChampionshipSettings = createAsyncThunk<void, UpsertChampionshipParams, { state: RootState }>(
  `${PREFIX}/upsertChampionshipSettings`,
  async (params, { dispatch, getState }) => {
    try {
      const state = getState();
      const championship = params?.id ? state.championships.championship.data!.byId[params.id] : null;

      const championshipId = await championshipRepository.upsertChampionship({
        id: championship?.id,
        tournamentId: params.tournamentId,
        name: championship?.name ?? params.name,
        description: championship?.description || null,
        startDate: championship?.startDate ?? params.startDate,
        endDate: championship?.endDate ?? params.endDate,
        address: championship?.address ?? params.address,
        settings: params.settings,
        teamIds: championship?.teams.map((team) => team.id) ?? [],
      });

      await dispatch(fetchChampionshipSettings(championshipId));
    } catch (err) {
      console.error(`${PREFIX}/upsertChampionshipSettings error:`, err);
      throw err;
    }
  },
);

export const addChampionshipTeam = createAsyncThunk<void, { teamId: number; championshipId: number }, { state: RootState }>(
  `${PREFIX}/addChampionshipTeam`,
  async (payload, { dispatch, getState }) => {
    try {
      const state = getState();
      const championship = state.championships.championship.data!.byId[payload.championshipId];
      const teamIds = new Set(championship.teams.map((team) => team.id));
      teamIds.add(payload.teamId);

      const params = {
        id: championship.id,
        tournamentId: championship.tournamentId,
        name: championship.name,
        description: championship.description || null,
        startDate: championship.startDate,
        endDate: championship.endDate,
        address: championship.address,
        settings: championship.settings,
        teamIds: Array.from(teamIds),
      };

      const championshipId = await championshipRepository.upsertChampionship(params);
      dispatch(fetchChampionship(championshipId));
    } catch (err) {
      console.error(`${PREFIX}/addChampionshipTeam error:`, err);
      throw err;
    }
  },
);

export const showChampionshipStageForms = createAction<{ state: boolean; championshipId: number }>(
  `${PREFIX}/showChampionshipStageForms`,
);
export const selectSymbolicTeam = createAction<{ symbolicTeam: SymbolicTeam | null; championshipId: number }>(
  `${PREFIX}/selectSymbolicTeam`,
);
export const showChampionshipSymbolicTeamForm = createAction<{ state: boolean; championshipId: number }>(
  `${PREFIX}/showChampionshipSymbolicTeamForm`,
);

export const upsertSymbolicTeam = createAsyncThunk<SymbolicTeam, UpsertSymbolicTeamDto>(
  `${PREFIX}/upsertSymbolicTeam`,
  async (params) => {
    try {
      return await symbolicTeamRepository.upsertSymbolicTeam(params);
    } catch (err) {
      console.error(`${PREFIX}/upsertSymbolicTeam error:`, err);
      throw err;
    }
  },
);

export const deleteChampionship = createAsyncThunk<boolean, { championshipId: number }>(
  `${PREFIX}/deleteChampionship`,
  async (payload, { rejectWithValue }) => {
    try {
      return await championshipRepository.deleteChampionship(payload.championshipId);
    } catch (error) {
      console.error(`${PREFIX}/deleteChampionship error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);
